import React, { useContext, useEffect, useState } from 'react';
import Section from '../../../components/Main/Section/Section';
import { useTranslation } from 'react-i18next';
import { CurrentViewContext } from '../../../providers/CurrentViewProvider';
import ActionBar from '../../../components/ActionBar/ActionBar';
import { Box, Button, LinearProgress, Typography, useTheme } from '@mui/material';
import { Add } from '@mui/icons-material';
import { IBenchmark } from '../../../shared/interfaces/IBenchmark';
import Benchmark from '../../../components/Benchmark/Benchmark';
import SortContainer from '../../../components/Containers/SortContainer';
import BenchmarkingService from '../../../services/BenchmarkingService';
import { ReactComponent as ResearchImage } from '../../../assets/images/research.svg';

const Benchmarking = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { setCurrentView } = useContext(CurrentViewContext);

  const [loading, setLoading] = useState(false);

  const [benchmarks, setBenchmarks] = useState<IBenchmark[]>([]);

  const onDelete = async (id: string) => {
    await BenchmarkingService.deleteBenchmark(id);
    await reload();
  };

  const reload = async () => {
    await getBenchmarks(new AbortController());
  };

  useEffect(() => {
    setCurrentView(t('nav_benchmarking'));
    document.title = `${t('app_branding')} ${t('nav_benchmarking')}`;
  }, [setCurrentView, t]);

  const getBenchmarks = async (controller: AbortController) => {
    const data = await BenchmarkingService.getBenchmarks(setLoading, controller.signal);
    if (!data) return;
    setBenchmarks(data);
  };

  useEffect(() => {
    const controller = new AbortController();

    (async () => await getBenchmarks(controller))();

    return () => {
      controller.abort();
    };
  }, []);

  return (
    <Section>
      <>
        {benchmarks.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              borderRadius: `${theme.shape.borderRadius}px`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <ResearchImage width="300" />
            <Typography sx={{ mt: theme.spacing(4) }}>Keine Einträge vorhanden</Typography>
          </Box>
        ) : (
          <>
            {loading ? (
              <LinearProgress />
            ) : (
              <>
                <ActionBar
                  children={
                    <>
                      <Box sx={{ display: 'flex', flexDirection: 'column', ml: theme.spacing(1) }}>
                        <Box sx={{ display: 'flex' }}>
                          <Typography variant={'body1'}>{t('benchmarking_headline')}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex' }}>
                          <Typography
                            color={theme.palette.text.secondary}
                            variant={'caption'}
                          >
                            {t('benchmarking_text')}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'none', marginLeft: 'auto' }}>
                        <Button
                          disabled
                          variant="contained"
                          startIcon={<Add />}
                        >
                          {t('new_benchmarking')}
                        </Button>
                      </Box>
                    </>
                  }
                />
                <SortContainer>
                  <Typography
                    variant="subtitle2"
                    sx={{ my: theme.spacing(2) }}
                  >
                    {benchmarks.length} Benchmarkings
                  </Typography>
                </SortContainer>
                {benchmarks.map((benchmark: IBenchmark) => {
                  return (
                    <Benchmark
                      key={benchmark.id}
                      id={benchmark.id}
                      label={benchmark.label}
                      platform={benchmark.platform}
                      subject={benchmark.subject}
                      competition={benchmark.competition}
                      onDelete={onDelete}
                      reload={reload}
                      createdAt={benchmark.createdAt}
                      updatedAt={benchmark.updatedAt}
                    />
                  );
                })}
              </>
            )}
          </>
        )}
      </>
    </Section>
  );
};

export default Benchmarking;
